import { FileDownloadOutlined, InsertDriveFileOutlined, OpenInNew } from "@mui/icons-material";
import { Chip } from "@mui/material";

import { palette } from "../theme/tokens";

import { openInNewTab } from "./embed-utils";

// A document, anywhere in the room, is a pill: a file icon, the document's
// name, a blue tint, and it opens in a new tab (2026-09-05, Bill: inline text
// links to PDFs and templates were confusing; a pill with a file icon says
// "this downloads a document" before you click). Used by the Onboarding
// Checklist (templates, uploads), the FAQ answers and the program modals.
//
// `inline` sits the pill inside running text (FAQ answers); the default is a
// block-level chip in a row of its own.
//
// `download` turns the pill into a save-the-file action (download icon, the
// anchor's `download` attribute, no new tab): used under the program modal's
// PDF viewer, where the pages are already on screen and the pill's only job
// is to hand over the file (Bill, 2026-09-06).
//
// `external` swaps the file icon for an open-in-new mark: a web page, not a
// document (the checklist's step links, 2026-10-27).

export function isDocumentHref(href: string): boolean {
    return /\/program-docs\//.test(href) || /\/uploads\//.test(href) || /\.(pdf|docx?|xlsx?|pptx?|csv)(\?|#|$)/i.test(href);
}

export function DocumentPill({
    href,
    label,
    inline,
    download,
    external,
}: {
    href: string;
    label: React.ReactNode;
    inline?: boolean;
    download?: boolean;
    external?: boolean;
}) {
    return (
        <Chip
            size="small"
            icon={download ? <FileDownloadOutlined fontSize="small" /> : external ? <OpenInNew fontSize="small" /> : <InsertDriveFileOutlined fontSize="small" />}
            label={label}
            component="a"
            clickable
            href={href}
            {...(download
                ? { download: true }
                : { target: "_blank", rel: "noopener noreferrer", onClick: openInNewTab(href) })}
            sx={{
                height: 26,
                fontSize: "0.75rem",
                fontWeight: 500,
                borderRadius: "6px",
                bgcolor: palette.infoBg,
                color: palette.blueDark,
                border: `1px solid ${palette.infoBorder}`,
                textDecoration: "none",
                "& .MuiChip-icon": { color: palette.blue, ml: "6px" },
                "&:hover": { bgcolor: "#E0EEFF" },
                ...(inline && { verticalAlign: "middle", mx: 0.25, my: 0.25 }),
            }}
        />
    );
}
